import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds, useMutation } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient from '../../utils/api';

interface WaterQualityFormData {
  pond_id: string;
  test_date: string;
  test_time?: string;
  ph_level?: number;
  ammonia?: number;
  oxygen?: number;
  no2?: number;
  no3?: number;
  remarks?: string;
  location_id?: string;
}

interface WaterQualityLog {
  id: string;
  pond_id: string;
  test_date: string;
  test_time?: string;
  ph_level?: number;
  ammonia?: number;
  oxygen?: number;
  no2?: number;
  no3?: number;
  remarks?: string;
  created_at: string;
  created_by: string;
}

interface PondWaterQualityData {
  id: string;
  name: string;
  pond_type: string;
  test_time: string;
  ph_level: string;
  ammonia: string;
  oxygen: string;
  no2: string;
  no3: string;
  remarks: string;
  expanded: boolean;
}

const WaterQualityForm: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [testDate, setTestDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [pondsData, setPondsData] = useState<PondWaterQualityData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { mutate: createWaterQualityLog, loading: creating } = useMutation<WaterQualityLog, WaterQualityFormData>();

  useEffect(() => {
    if (ponds) {
      const currentTime = new Date().toTimeString().split(' ')[0].slice(0, 5);
      setPondsData(ponds.map(pond => ({
        id: pond.id,
        name: pond.name,
        pond_type: pond.pond_type,
        test_time: currentTime,
        ph_level: '',
        ammonia: '',
        oxygen: '',
        no2: '',
        no3: '',
        remarks: '',
        expanded: false
      })));
    }
  }, [ponds]);

  const updatePond = (pondId: string, field: keyof PondWaterQualityData, value: string | boolean) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, [field]: value } : pond
    ));
  };

  const togglePondExpansion = (pondId: string) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, expanded: !pond.expanded } : pond
    ));
  };

  const handleVoiceInput = (pondId: string) => {
    alert(`Voice input for ${pondId} - Feature would integrate with device microphone`);
  };

  // Bulk actions for efficiency
  const copyToAllPonds = (field: keyof PondWaterQualityData, value: string) => {
    setPondsData(pondsData.map(pond => ({ ...pond, [field]: value })));
  };

  const clearAllPonds = () => {
    setPondsData(pondsData.map(pond => ({ 
      ...pond, 
      ph_level: '', 
      ammonia: '', 
      oxygen: '',
      no2: '',
      no3: '',
      remarks: '' 
    })));
  };

  const getParameterStatus = (param: string, value?: number) => {
    if (value === undefined || value === null) return null;
    
    let status = 'normal';
    let color = 'bg-green-100 text-green-800';
    
    switch (param) {
      case 'ph':
        if (value < 6.5 || value > 8.5) {
          status = 'warning';
          color = 'bg-yellow-100 text-yellow-800';
        }
        if (value < 5.5 || value > 9.5) {
          status = 'critical';
          color = 'bg-red-100 text-red-800';
        }
        break;
      case 'ammonia':
        if (value > 0.25) {
          status = 'warning';
          color = 'bg-yellow-100 text-yellow-800';
        }
        if (value > 0.5) {
          status = 'critical';
          color = 'bg-red-100 text-red-800';
        }
        break;
      case 'oxygen':
        if (value < 5) {
          status = 'warning';
          color = 'bg-yellow-100 text-yellow-800';
        }
        if (value < 3) {
          status = 'critical';
          color = 'bg-red-100 text-red-800';
        }
        break;
    }
    
    return { status, color };
  };

  const handleSubmit = async () => {
    const validEntries = pondsData.filter(pond => 
      pond.ph_level || pond.ammonia || pond.oxygen || pond.no2 || pond.no3
    );
    
    if (validEntries.length === 0) {
      alert('Please fill water quality data for at least one pond');
      return;
    }

    try {
      setLoading(true);
      const promises = validEntries.map(pond => 
        createWaterQualityLog('/water-quality-logs', {
          pond_id: pond.id,
          test_date: testDate,
          test_time: pond.test_time || undefined,
          ph_level: pond.ph_level ? parseFloat(pond.ph_level) : undefined,
          ammonia: pond.ammonia ? parseFloat(pond.ammonia) : undefined,
          oxygen: pond.oxygen ? parseFloat(pond.oxygen) : undefined,
          no2: pond.no2 ? parseFloat(pond.no2) : undefined,
          no3: pond.no3 ? parseFloat(pond.no3) : undefined,
          remarks: pond.remarks || undefined,
          location_id: selectedLocation?.id,
        })
      );
      
      await Promise.all(promises);
      alert(`✅ Successfully recorded water quality data for ${validEntries.length} ponds on ${testDate}`);
      
      // Reset form
      setPondsData(pondsData.map(pond => ({ 
        ...pond, 
        ph_level: '', 
        ammonia: '', 
        oxygen: '',
        no2: '',
        no3: '',
        remarks: '' 
      })));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getFilledPondsCount = () => {
    return pondsData.filter(pond => 
      pond.ph_level || pond.ammonia || pond.oxygen || pond.no2 || pond.no3
    ).length;
  };

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-800">💧 Water Quality - EOD</h1>
        </div>
        
        {/* Date Selection */}
        <div className="flex items-center gap-2 mb-3">
          <input
            type="date"
            value={testDate}
            onChange={(e) => setTestDate(e.target.value)}
            className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={clearAllPonds}
            className="bg-red-100 text-red-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
          >
            <span className="text-xs">🔄</span>
            Clear All
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="bg-blue-50 p-2 rounded-lg">
          <div className="flex justify-between text-xs text-blue-700 mb-1">
            <span>Progress</span>
            <span>{getFilledPondsCount()}/{pondsData.length} ponds completed</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(getFilledPondsCount() / pondsData.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Ponds List */}
      <div className="p-4 space-y-3">
        {pondsData.map((pond) => {
          const phStatus = getParameterStatus('ph', parseFloat(pond.ph_level));
          const oxygenStatus = getParameterStatus('oxygen', parseFloat(pond.oxygen));
          const ammoniaStatus = getParameterStatus('ammonia', parseFloat(pond.ammonia));
          
          return (
            <div key={pond.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Pond Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{pond.name}</h3>
                    <p className="text-sm text-gray-500">{pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}</p>
                  </div>
                  <button
                    onClick={() => togglePondExpansion(pond.id)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    {pond.expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Entry Fields */}
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      pH Level
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="14"
                      value={pond.ph_level}
                      onChange={(e) => updatePond(pond.id, 'ph_level', e.target.value)}
                      placeholder="7.0"
                      className={`w-full p-3 border rounded-lg text-center text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        phStatus?.status === 'critical' ? 'border-red-300 bg-red-50' :
                        phStatus?.status === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                        'border-gray-300'
                      }`}
                    />
                    {phStatus && (
                      <div className={`text-xs px-2 py-1 rounded mt-1 ${phStatus.color}`}>
                        {phStatus.status}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Dissolved O₂ (mg/L)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={pond.oxygen}
                      onChange={(e) => updatePond(pond.id, 'oxygen', e.target.value)}
                      placeholder="6.0"
                      className={`w-full p-3 border rounded-lg text-center text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        oxygenStatus?.status === 'critical' ? 'border-red-300 bg-red-50' :
                        oxygenStatus?.status === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                        'border-gray-300'
                      }`}
                    />
                    {oxygenStatus && (
                      <div className={`text-xs px-2 py-1 rounded mt-1 ${oxygenStatus.color}`}>
                        {oxygenStatus.status}
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded Fields */}
                {pond.expanded && (
                  <div className="space-y-3 border-t border-gray-100 pt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Test Time
                      </label>
                      <input
                        type="time"
                        value={pond.test_time}
                        onChange={(e) => updatePond(pond.id, 'test_time', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Ammonia (mg/L)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={pond.ammonia}
                          onChange={(e) => updatePond(pond.id, 'ammonia', e.target.value)}
                          placeholder="0.1"
                          className={`w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            ammoniaStatus?.status === 'critical' ? 'border-red-300 bg-red-50' :
                            ammoniaStatus?.status === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                            'border-gray-300'
                          }`}
                        />
                        {ammoniaStatus && (
                          <div className={`text-xs px-2 py-1 rounded mt-1 ${ammoniaStatus.color}`}>
                            {ammoniaStatus.status}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          NO₂ (mg/L)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={pond.no2}
                          onChange={(e) => updatePond(pond.id, 'no2', e.target.value)}
                          placeholder="0.0"
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        NO₃ (mg/L)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={pond.no3}
                        onChange={(e) => updatePond(pond.id, 'no3', e.target.value)}
                        placeholder="5.0"
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-700">
                          Remarks (Optional)
                        </label>
                        <button
                          onClick={() => handleVoiceInput(pond.id)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          <span className="text-sm">🎤</span>
                        </button>
                      </div>
                      <textarea
                        value={pond.remarks}
                        onChange={(e) => updatePond(pond.id, 'remarks', e.target.value)}
                        placeholder="Water clarity, temperature, weather conditions, etc."
                        rows={2}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary & Submit */}
      <div className="p-4 bg-white border-t border-gray-200 sticky bottom-0">
        <div className="grid grid-cols-1 gap-4 mb-4">
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <div className="text-xs text-blue-600 font-medium">Water Quality Tests</div>
            <div className="text-lg font-bold text-blue-700">{getFilledPondsCount()} ponds tested</div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading || creating}
          className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading || creating ? <LoadingSpinner size="sm" /> : <span className="text-lg">💾</span>}
          Submit EOD Water Quality ({getFilledPondsCount()}/{pondsData.length})
        </button>
      </div>
    </div>
  );
};

export default WaterQualityForm;